import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { ApplicationConfigurationService } from "@app/core/services/ApplicationConfiguration.service";

const ASSIGNMENT_URL = "/api/v1/phms/hearing-official-assignments";

const quietHttpOptions = {
  headers: new HttpHeaders({
    "skip-error-interceptor": "yes",
    "skip-loading-interceptor": "yes",
  }),
  withCredentials: true,
};

/** The parole official assigned to a scheduled hearing (story 1444). */
export interface HearingOfficialAssignment {
  id: number;
  scheduledProceedingId: number;
  officialUserId: number;
  isPresiding?: boolean;
  version?: number;
  officialDisplayName: string;
  officialEmail: string | null;
  assignedByUserId: number | null;
  assignedAtUtc: string;
}

/** An active user holding the parole_official role, offered in the assign picker. */
export interface AssignableOfficial {
  userId: number;
  displayName: string;
  email: string | null;
}

/**
 * Hearing-official assignment API (story 1444). Reads are gated server-side on
 * workflow_runtime.read (anyone who can open hearing detail); assign/replace/unassign and the
 * officials picker are gated on workflow_runtime.assign (schedulers/admins) — the client gate is
 * UX only and the server stays authoritative.
 */
@Injectable({ providedIn: "root" })
export class HearingOfficialAssignmentService {
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(ApplicationConfigurationService);

  getMembers(id: number): Observable<HearingOfficialAssignment[]> {
    return this.http.get<HearingOfficialAssignment[]>(this.appConfig.apiUrl(`${ASSIGNMENT_URL}/${id}/members`), quietHttpOptions);
  }

  saveMembers(id: number, members: { officialUserId: number; isPresiding: boolean }[], current: HearingOfficialAssignment[]): Observable<HearingOfficialAssignment[]> {
    return this.http.put<HearingOfficialAssignment[]>(this.appConfig.apiUrl(`${ASSIGNMENT_URL}/${id}/members`),
      { members, expectedVersions: Object.fromEntries(current.map(item => [item.id, item.version])) }, quietHttpOptions);
  }

  /** The hearing's assigned official; 404s when the hearing has no assignment (caller catches). */
  getAssignment(scheduledProceedingId: number): Observable<HearingOfficialAssignment> {
    return this.http.get<HearingOfficialAssignment>(
      this.appConfig.apiUrl(`${ASSIGNMENT_URL}/${encodeURIComponent(String(scheduledProceedingId))}`),
      quietHttpOptions,
    );
  }

  /** Officials assignable to hearings (active parole_official users), for the assign picker. */
  getOfficials(): Observable<AssignableOfficial[]> {
    return this.http.get<AssignableOfficial[]>(
      this.appConfig.apiUrl(`${ASSIGNMENT_URL}/officials`),
      quietHttpOptions,
    );
  }

  /** Assign (or replace) the hearing's official — one official per hearing. */
  assign(scheduledProceedingId: number, officialUserId: number): Observable<HearingOfficialAssignment> {
    return this.http.put<HearingOfficialAssignment>(
      this.appConfig.apiUrl(`${ASSIGNMENT_URL}/${encodeURIComponent(String(scheduledProceedingId))}`),
      { officialUserId },
      quietHttpOptions,
    );
  }

  /** Remove the hearing's official assignment. */
  unassign(scheduledProceedingId: number): Observable<void> {
    return this.http.delete<void>(
      this.appConfig.apiUrl(`${ASSIGNMENT_URL}/${encodeURIComponent(String(scheduledProceedingId))}`),
      quietHttpOptions,
    );
  }
}
